import { PaymentMethodsServerKeys, ResponseChannel, CompanyCodes } from "@/interfaces/rapydTypes";
import React from "react";
import { useTranslation } from "next-i18next";

import handleDeepLink from "@/utils/handleDeepLink";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { useRouter } from "next/router";
import { helpCenterLocaleHost } from "@/utils/util";
import clsx from "clsx";
import Skeleton from "@/components/skeleton/Skeleton";
import Divider from "@/components/divider";
import { BackIcon, QaIcon } from "@/components/RFIcon";
import PaymentChannelIcon from "./PaymentChannelIcon";

interface PaymentCardProps {
	info?: ResponseChannel;
	currency?: string;
	showRecommend?: boolean;
	onCardClick?: (info?: ResponseChannel, title?: string) => void;
}

export default function PaymentCard({ onCardClick, info, currency, showRecommend }: PaymentCardProps) {
	const { t } = useTranslation("rapyd");
	const router = useRouter();

	const renderChannelNameLocale = () => {
		switch (info?.channelName) {
			case PaymentMethodsServerKeys.CARD: {
				if (info?.companyCode === CompanyCodes.DCS) {
					return t("dcs-credit-card");
				}
				return t("card-title");
			}
			case PaymentMethodsServerKeys.BANK:
				return t("online-banking-title");
			case PaymentMethodsServerKeys.E_WALLET:
				return t("eWallet-title");
			case PaymentMethodsServerKeys.WIRE_TRANSFER:
				return t("wire-transfer-title");
			case PaymentMethodsServerKeys.SG_WIRE_TRANSFER:
				return t("sg-wire-transfer-title");
			case PaymentMethodsServerKeys.HK_WIRE_TRANSFER: {
				return currency === "USD" ? t("HK-account-local-transfer") : t("FPS-transfer");
			}
			default:
				return "";
		}
	};

	const renderChannelDescriptionLocale = () => {
		switch (info?.channelName) {
			case PaymentMethodsServerKeys.CARD: {
				if (info?.companyCode === CompanyCodes.DCS) {
					return t("supports-dcs-credit-card-deposits");
				}
				return t("card-description");
			}
			case PaymentMethodsServerKeys.BANK:
				return t("online-banking-description");
			case PaymentMethodsServerKeys.E_WALLET:
				return t("eWallet-description");
			case PaymentMethodsServerKeys.WIRE_TRANSFER:
				return t("wire-transfer-description");
			case PaymentMethodsServerKeys.SG_WIRE_TRANSFER:
				return t("sg-wire-transfer-description");
			case PaymentMethodsServerKeys.HK_WIRE_TRANSFER: {
				if (currency === "USD") {
					return t("instant-USD-transfers-between-HK-accounts");
				}
				return t("instant-inter-bank-transfer-between-hong-kong-accounts");
			}

			default:
				return "";
		}
	};

	const renderIcon = () => {
		if (!info) {
			return <Skeleton className="w-10 h-10 rounded-md" />;
		}

		return <PaymentChannelIcon channelName={info.channelName} companyCode={info.companyCode} />;
	};

	const renderTitle = () => {
		if (!info) {
			return <Skeleton className="w-[210px] h-4 rounded-md" />;
		}

		return (
			<p className="text-sm font-bold">
				{renderChannelNameLocale()}
				{renderRecommend()}
				{renderWireTransferHelp()}
			</p>
		);
	};

	const renderRecommend = () => {
		if (!showRecommend) return null;
		return (
			<span>
				{" ( "} {t("recommend")} {" )"}
			</span>
		);
	};

	const renderWireTransferHelp = () => {
		if (info?.channelName !== PaymentMethodsServerKeys.WIRE_TRANSFER) {
			return null;
		}

		return (
			<QaIcon
				className="ml-1.5 align-[-2px] w-3.5 h-3.5"
				onClick={(e: any) => {
					e.stopPropagation();
					showHelpDetail();
				}}
			/>
		);
	};

	const showHelpDetail = () => {
		const url = `${helpCenterLocaleHost(router.locale)}/docs/wire-transfer/guidance/`;
		handleDeepLink(clientDeepLinks.webview(url));
	};

	const renderDescription = () => {
		if (!info) {
			return <Skeleton className="w-[234px] h-[18px] rounded-sm mt-1" />;
		}
		return (
			<p
				className="text-xs font-semibold inter-text"
				dangerouslySetInnerHTML={{ __html: renderChannelDescriptionLocale() }}
			/>
		);
	};

	const renderArrow = () => {
		if (!info) {
			return <Skeleton className="w-6 h-6 rounded-md" />;
		}

		if (!info.support) return null;

		return <BackIcon className="c3_53_text rotate-180 h-5" />;
	};

	const renderExtra = () => {
		if (!info) {
			return (
				<>
					<Skeleton className="w-full h-6 rounded-md mt-1" />
					<Skeleton className="w-full h-6 mt-1 rounded-md" />
				</>
			);
		}
		switch (info.channelName) {
			case PaymentMethodsServerKeys.WIRE_TRANSFER:
				return (
					<>
						<ExtraText y="12px" text={t("banks-in-china-not-supported")} />
					</>
				);
			case PaymentMethodsServerKeys.CARD: {
				if (info?.companyCode === CompanyCodes.DCS) {
					return (
						<>
							<ExtraText y="10px" text={t("quick-deposit-with-fee", { free: info.feeRate })} />
						</>
					);
				}
				return (
					<>
						<ExtraText y="12px" text={t("except-issued-in-china-and-vietnam")} />
						<ExtraText text={t("arrives-in-real-time-the-payment-service-provider")} />
						<ExtraText text={t("recommended-when-deposit-over-1000-usd")} />
					</>
				);
			}

			case PaymentMethodsServerKeys.HK_WIRE_TRANSFER: {
				if (currency === "USD") {
					return (
						<>
							<ExtraText y="12px" text={t("the-bank-charges-as-handling-fee")} />
							<ExtraText text={t("deposits-of-or-more-are-eligible-for-a-transaction-fee-rebate")} />
							<ExtraText text={t("no-fee-for-HSBC-account")} />
						</>
					);
				} else {
					return <ExtraText y="12px" text={t("no-handling-fee")} />;
				}
			}
			default:
				return null;
		}
	};

	return (
		<div
			className={clsx("py-5 relative pr-4.5", info?.support ? "opacity-100" : "opacity-40")}
			onClick={() => onCardClick && onCardClick(info, renderChannelNameLocale())}
		>
			<div className="flex items-center">
				{renderIcon()}

				<div className="flex-1 px-3">
					{renderTitle()}
					{renderDescription()}
				</div>

				{renderArrow()}
			</div>

			{renderExtra()}
			<Divider className="absolute bottom-0 right-0 w-full" />
		</div>
	);
}

function ExtraText({ text, y = "2px" }: { text: string; y?: string }) {
	return (
		<p
			style={{
				marginTop: y,
			}}
			className="relative pl-5 text-xxs1 font-bold inter-text before:absolute before:left-2 before:top-[7px] 
              before:h-1 before:w-1 before:rounded-full
            before:bg-[#787878] dark:before:bg-[#858585] before:content-['']"
		>
			{text}
		</p>
	);
}
